/**
 * LearnMoreBanner Component
 *
 * Educational banner that provides real value upfront and links to more content.
 * Configurable for different drill types (intervals, chords, etc.)
 */

import React from "react";
import { Link } from "react-router-dom";
import styles from "../styles/LearnMoreBanner.module.css";

interface BannerExample {
    primary: string;
    secondary: string;
}

interface LearnMoreBannerProps {
    to: string;
    variant?: "intro" | "completion";
    // For completion variant
    title?: string;
    description?: string;
    // For intro variant (configurable content)
    introTitle?: string;
    introText?: React.ReactNode;
    examplesLabel?: string;
    examples?: BannerExample[];
}

export default function LearnMoreBanner({
    to,
    variant = "intro",
    title,
    description,
    introTitle,
    introText,
    examplesLabel,
    examples,
}: LearnMoreBannerProps) {
    if (variant === "completion") {
        return (
            <Link to={to} className={styles.completionBanner}>
                <div className={styles.completionContent}>
                    <span className={styles.completionTitle}>{title || "Want to improve?"}</span>
                    <span className={styles.completionDescription}>
                        {description || "Check out the learning guide"}
                    </span>
                </div>
                <div className={styles.completionArrow}>
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <path
                            d="M7.5 15L12.5 10L7.5 5"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                </div>
            </Link>
        );
    }

    // Intro variant - actual education with examples
    return (
        <div className={styles.introCard}>
            <div className={styles.introMain}>
                <h3 className={styles.introTitle}>{introTitle}</h3>
                <p className={styles.introText}>
                    {introText}
                </p>
                <Link to={to} className={styles.introLink}>
                    Learn more in the full guide
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                        <path
                            d="M6 12L10 8L6 4"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                </Link>
            </div>
            {examples && examples.length > 0 && (
                <div className={styles.introExamples}>
                    {examplesLabel && <div className={styles.examplesLabel}>{examplesLabel}</div>}
                    <div className={styles.examplesList}>
                        {examples.map((example, index) => (
                            <div key={index} className={styles.exampleItem}>
                                <span className={styles.exampleSong}>{example.primary}</span>
                                <span className={styles.exampleInterval}>{example.secondary}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
